import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { get } from "lodash";
import React, { FC, useMemo } from "react";
import SummaryLine from "src/components/ui-component/form-component/abschluss-page/SummaryLine";
import { useApplication } from "src/context/ApplicationContext";
import MittelabrufSchema from "src/lib/paths/mittelabruf_FULL_Paths.json";
import ApplicationHeading from "src/pages/private/online-antrag/application-heading/ApplicationHeading";
import { DisplayDecimal, DisplayEuro } from "src/utils/formats";

const P = MittelabrufSchema.attributes;

type Row = {
  label: string;
  value: string;
};

const toNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatEuro = (value: unknown): string => DisplayEuro(toNumber(value));
const formatRate = (value: unknown): string =>
  `${DisplayDecimal(toNumber(value))} %`;

const MittelabrufLoeweMA: FC = () => {
  const { application } = useApplication();

  const { organizations, rows, alreadyPaid, maximumRequest } = useMemo(() => {
    const organizationsRows = get(application, P.Kalkulation_Block_1.path, []);
    const alreadyPaidValue = toNumber(
      get(
        application,
        P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path,
      ),
    );
    const approvedFunding = toNumber(
      get(
        application,
        P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path,
      ),
    );
    const proportionalFunding = organizationsRows.reduce(
      (sum: number, row: any) => sum + toNumber(get(row, "Zelle_4")),
      0,
    );
    const maxRequest = Math.max(
      0,
      Math.min(
        proportionalFunding - alreadyPaidValue,
        approvedFunding - alreadyPaidValue,
      ),
    );
    const total = (field: string): number =>
      organizationsRows.reduce(
        (sum: number, row: any) => sum + toNumber(get(row, field)),
        0,
      );

    const tableRows: Row[] = [
      {
        label: "Individuelle Förderquote gemäß gültigem Finanzierungsplan",
        value: "Zelle_2",
      },
      {
        label: "Getätigte Ausgaben im aktuellen Haushaltsjahr",
        value: "Zelle_3",
      },
      {
        label: "Vorausschau der folgenden drei Monate",
        value: "Zelle_5",
      },
      {
        label: "Summe",
        value: "Zelle_6",
      },
      {
        label: "Zuwendung anteilig gemäß individueller Förderquote",
        value: "Zelle_4",
      },
    ];

    return {
      organizations: organizationsRows,
      rows: tableRows.map((row) => ({
        ...row,
        total: row.value === "Zelle_2" ? null : total(row.value),
      })),
      alreadyPaid: alreadyPaidValue,
      maximumRequest: maxRequest,
    };
  }, [application]);

  return (
    <>
      <ApplicationHeading title="Mittelabruf" />
      <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
        Die Berechnung des Mittelabrufs basiert auf den von Ihnen gemachten
        Angaben, wird automatisch generiert und steht unter dem Vorbehalt einer
        fachlichen Prüfung durch die Hessen Agentur.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            aria-label="Mittelabruf Übersicht"
            sx={{
              minWidth: Math.max(760, 280 + organizations.length * 125),
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: "36%",
                    whiteSpace: "nowrap",
                    backgroundColor: "#f2f2f7",
                  }}
                >
                  Projektpartner
                </TableCell>
                {organizations.map((organization: any) => (
                  <TableCell
                    key={organization.id}
                    align="right"
                    sx={{
                      minWidth: 125,
                      width: "12%",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f2f2f7",
                    }}
                  >
                    {get(organization, "Zelle_1", "")}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    minWidth: 125,
                    width: "12%",
                    whiteSpace: "nowrap",
                    backgroundColor: "#f2f2f7",
                  }}
                >
                  Gesamt
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell
                    sx={{
                      fontWeight:
                        row.label === "Summe" ||
                        row.label.startsWith("Zuwendung")
                          ? 600
                          : 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.label}
                  </TableCell>
                  {organizations.map((organization: any) => (
                    <TableCell key={organization.id} align="right">
                      {row.value === "Zelle_2"
                        ? formatRate(get(organization, row.value))
                        : formatEuro(get(organization, row.value))}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight:
                        row.label === "Summe" ||
                        row.label.startsWith("Zuwendung")
                          ? 600
                          : 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.value === "Zelle_2" ? "—" : formatEuro(row.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 2, mt: 6 }}>
        <SummaryLine
          label="Bereits ausgezahlte Fördermittel für das gleiche Haushaltsjahr (abzgl.)"
          value={formatEuro(alreadyPaid)}
          alignValueRight
        />
        <SummaryLine
          label="berechnete max. Förderung / Mittelabruf (vorbehaltlich einer fachlichen Prüfung durch die Hessen Agentur)"
          value={formatEuro(maximumRequest)}
          boldLabel
          showBackground
          alignValueRight
        />
      </Box>
    </>
  );
};

export default MittelabrufLoeweMA;
